// src/services/api.ts

// Använd environment variables eller fallback till localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7001/api';

// Interface för Contact-data
interface ContactFormData {
  name: string;
  email: string;
  company?: string;
  phone?: string;
  message: string;
  language?: string;
  captchaToken: string;
}

interface CreateContactRequest {
  name: string;
  email: string;
  phone?: string;
  message: string;
  company?: string;
  language?: string;
  captchaToken: string;
}


interface Contact {
  id: number;
  name: string;
  email: string;
  phone?: string;
  message: string;
  company?: string;
  language: string;
  createdAt: string;
}

// Error handling helper
class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public data?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Helper function för API-anrop
async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });

    // Om response inte är ok, kasta error
    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      
      // Försök att extrahera error message från response
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.title || errorMessage;
        throw new ApiError(errorMessage, response.status, errorData);
      } catch {
        throw new ApiError(errorMessage, response.status);
      }
    }

    // Om det finns content, parsa JSON
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }
    
    // Returnera tom response för 204 No Content etc
    return {} as T;
  } catch (error) {
    // Om det är en ApiError, kasta vidare
    if (error instanceof ApiError) {
      throw error;
    }
    
    // För nätverksfel etc
    if (error instanceof Error) {
      throw new ApiError(
        `Kunde inte ansluta till servern: ${error.message}`
      );
    }
    
    throw new ApiError('Ett okänt fel uppstod');
  }
}

// Contact Service
export const contactService = {
  /**
   * Skickar kontaktformulär till backend
   */
  async sendContactForm(data: ContactFormData): Promise<Contact> {
    const request: CreateContactRequest = {
      name: data.name,
      email: data.email,
      phone: data.phone || undefined,
      message: data.message,
      company: data.company || undefined,
      language: data.language || 'sv',
      captchaToken: data.captchaToken
    };

    return fetchApi<Contact>('/contacts', {
      method: 'POST',
      body: JSON.stringify(request)
    });
  },

  /**
   * Hämtar alla kontakter (kräver eventuellt autentisering)
   */
  async getAllContacts(): Promise<Contact[]> {
    return fetchApi<Contact[]>('/contacts', {
      method: 'GET'
    });
  },

  /**
   * Hämtar en specifik kontakt
   */
  async getContactById(id: number): Promise<Contact> {
    return fetchApi<Contact>(`/contacts/${id}`, {
      method: 'GET'
    });
  },

  /**
   * Tar bort en kontakt (kräver eventuellt autentisering)
   */
  async deleteContact(id: number): Promise<void> {
    return fetchApi<void>(`/contacts/${id}`, {
      method: 'DELETE'
    });
  }
};

// Project Service
export const projectService = {
  async getAllProjects() {
    return fetchApi('/projects', {
      method: 'GET'
    });
  },

  async getProjectById(id: number) {
    return fetchApi(`/projects/${id}`, {
      method: 'GET'
    });
  },

  async getActiveProjects() {
    return fetchApi('/projects/active', {
      method: 'GET'
    });
  }
};

// Case Study Service
export const caseStudyService = {
  async getAllCaseStudies() {
    return fetchApi('/casestudies', {
      method: 'GET'
    });
  },

  async getCaseStudyById(id: number) {
    return fetchApi(`/casestudies/${id}`, {
      method: 'GET'
    });
  },

  async getFeaturedCaseStudies() {
    return fetchApi('/casestudies/featured', {
      method: 'GET'
    });
  }
};

// Health Check
export const healthService = {
  /**
   * Kontrollerar om API:et är tillgängligt
   */
  async checkHealth(): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/health`, {
        method: 'GET'
      });
      return response.ok;
    } catch {
      return false;
    }
  }
};

// Export types
export type { ContactFormData, CreateContactRequest, Contact, ApiError };